import { useState, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { VoiceSetup } from "@/components/VoiceSetup";
import api, { API, formatApiErrorDetail } from "@/lib/api";
import { Page, PageHeader } from "@/components/PageHeader";
import { Copy, ArrowSquareOut, Buildings, Palette, Plugs, Clock, DeviceMobile, CloudArrowDown, UploadSimple, PencilSimple, Waveform } from "@phosphor-icons/react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const resolveImg = (src) => (src && src.startsWith("/api") ? `${BACKEND_URL}${src}` : src);

export default function Settings() {
  const { property, user } = useAuth();
  const [copied, setCopied] = useState(false);
  const { data: emailStatus } = useQuery({ queryKey: ["email-status"], queryFn: async () => (await api.get("/email/status")).data });
  const { data: pmsStatus } = useQuery({ queryKey: ["pms-status"], queryFn: async () => (await api.get("/pms/status")).data });
  const { data: voiceStatus } = useQuery({ queryKey: ["voice-status"], queryFn: async () => (await api.get("/voice/status")).data });
  if (!property) return <Page />;

  const widgetUrl = `${window.location.origin}/widget/${property.id}`;
  const snippet = `<script src="${window.location.origin}/embed.js"
  data-property="${property.id}"
  data-color="${property.brand_color}"></script>`;

  const copy = (text) => { navigator.clipboard.writeText(text); setCopied(true); toast.success("Copied to clipboard"); setTimeout(() => setCopied(false), 1500); };

  const integrations = [
    { name: "OPERA Cloud (OHIP)", desc: "PMS · guest & reservation sync", connected: !!pmsStatus?.ohip_configured },
    { name: "SMTP2GO", desc: "Email delivery & open/click tracking", connected: !!emailStatus?.configured },
    { name: "xAI Grok Voice", desc: "AI voice agent · inbound call logging", connected: !!voiceStatus?.configured },
    { name: "Google Business Profile", desc: "Review ingestion", connected: false },
  ];

  return (
    <Page>
      <PageHeader title="Settings" subtitle="Property profile, integrations & embeddable widget." />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 space-y-4">
          <div className="rounded-lg border border-border bg-card overflow-hidden fade-up">
            <img src={resolveImg(property.image)} alt="" className="h-40 w-full object-cover bg-secondary" />
            <div className="p-5">
              <div className="flex items-center justify-between gap-2 mb-2">
                <div className="flex items-center gap-2 text-muted-foreground text-[11px] uppercase tracking-[0.14em] font-semibold"><Buildings size={14} /> Property</div>
                {user?.is_admin && <EditPropertyDialog property={property} />}
              </div>
              <h3 className="font-heading text-xl font-semibold" data-testid="property-name">{property.name}</h3>
              <p className="text-sm text-muted-foreground mt-1" data-testid="property-address">{property.address}</p>
              <div className="grid grid-cols-2 gap-3 mt-4">
                <Info label="Owner" value={user?.name} />
                <Info label="Contact" value={user?.email} />
                <Info label="Brand color" value={property.brand_color} swatch={property.brand_color} />
                <Info label="Property ID" value={property.id.slice(0, 8) + "…"} />
              </div>
            </div>
          </div>

          <div className="rounded-lg border border-border bg-card p-5 fade-up" style={{ animationDelay: "60ms" }}>
            <div className="flex items-center gap-2 text-muted-foreground text-[11px] uppercase tracking-[0.14em] font-semibold mb-3"><Plugs size={14} /> Integrations</div>
            <div className="space-y-2.5">
              {integrations.map((it) => (
                <div key={it.name} data-testid={`integration-${it.name.split(" ")[0].toLowerCase()}`} className="flex items-center justify-between rounded-md border border-border px-3.5 py-3">
                  <div><div className="font-medium text-sm">{it.name}</div><div className="text-xs text-muted-foreground">{it.desc}</div></div>
                  {it.connected
                    ? <span className="text-[11px] px-2 py-0.5 rounded-full bg-success/10 text-success border border-success/20 flex items-center gap-1"><span className="h-1.5 w-1.5 rounded-full bg-success" /> Connected</span>
                    : <span className="text-[11px] px-2 py-0.5 rounded-full bg-secondary text-muted-foreground border border-border">Add key later</span>}
                </div>
              ))}
            </div>
          </div>

          <PmsSync pmsStatus={pmsStatus} />
        </div>

        <div className="space-y-4">
          <MessagingSchedule property={property} />
          <div className="rounded-lg border border-border bg-card p-5 fade-up" style={{ animationDelay: "40ms" }}>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2 text-muted-foreground text-[11px] uppercase tracking-[0.14em] font-semibold"><Palette size={14} /> Chat widget</div>
              {user?.is_admin && <CustomizeWidgetDialog propertyId={property.id} brandColor={property.brand_color} />}
            </div>
            <p className="text-sm text-muted-foreground mb-3">Embed the AI concierge on your website with one snippet.</p>
            <pre data-testid="embed-snippet" className="text-[11px] bg-foreground text-background rounded-md p-3 overflow-x-auto leading-relaxed">{snippet}</pre>
            <div className="flex gap-2 mt-3">
              <Button data-testid="copy-embed-btn" variant="secondary" size="sm" className="gap-1.5 flex-1" onClick={() => copy(snippet)}><Copy size={14} /> {copied ? "Copied!" : "Copy code"}</Button>
              <Button data-testid="preview-widget-btn" size="sm" className="gap-1.5 flex-1" onClick={() => window.open(widgetUrl, "_blank")}><ArrowSquareOut size={14} /> Preview</Button>
            </div>
          </div>
        </div>
      </div>

      {/* Voice Agent setup */}
      <div className="mt-4" data-testid="settings-voice-agent">
        <div className="flex items-center gap-2 text-muted-foreground text-[11px] uppercase tracking-[0.14em] font-semibold mb-3"><Waveform size={14} /> Voice Agent</div>
        <VoiceSetup />
      </div>

      {/* Security */}
      <div className="mt-4" data-testid="settings-security">
        <div className="flex items-center gap-2 text-muted-foreground text-[11px] uppercase tracking-[0.14em] font-semibold mb-3"><Plugs size={14} /> Security</div>
        <ChangePasswordCard />
      </div>
    </Page>
  );
}

function CustomizeWidgetDialog({ propertyId, brandColor }) {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [welcome, setWelcome] = useState("");
  const [avatar, setAvatar] = useState("");
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get(`/widget/${propertyId}`);
      setTitle(data.title || "");
      setWelcome(data.welcome_message || "");
      setAvatar(data.avatar || "");
    } catch { /* ignore */ }
  };

  const onOpen = (v) => { setOpen(v); if (v) load(); };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/property/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setAvatar(data.url);
      toast.success("Avatar uploaded");
    } catch { toast.error("Upload failed"); }
    finally { setUploading(false); }
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/property/widget", { title, welcome_message: welcome, avatar });
      toast.success("Widget updated");
      setOpen(false);
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not save"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpen}>
      <DialogTrigger asChild>
        <Button data-testid="customize-widget-btn" variant="outline" size="sm" className="gap-1.5"><PencilSimple size={14} /> Customize</Button>
      </DialogTrigger>
      <DialogContent data-testid="customize-widget-dialog">
        <DialogHeader>
          <DialogTitle>Customize chat widget</DialogTitle>
          <DialogDescription>Personalize how the concierge appears on your website. The accent color follows your brand color.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-1">
          <div className="flex items-center gap-3">
            <div className="h-12 w-12 rounded-full flex items-center justify-center text-white overflow-hidden shrink-0" style={{ background: brandColor || "#EA580C" }}>
              {avatar ? <img src={resolveImg(avatar)} alt="" className="h-12 w-12 object-cover" /> : <span className="text-lg font-semibold">{(title || "H").slice(0, 1)}</span>}
            </div>
            <div className="flex items-center gap-2">
              <Button type="button" variant="outline" size="sm" disabled={uploading} onClick={() => fileRef.current?.click()} data-testid="widget-avatar-upload">
                <UploadSimple size={14} className="mr-1.5" /> {uploading ? "Uploading…" : "Upload avatar"}
              </Button>
              {avatar && <Button type="button" variant="ghost" size="sm" onClick={() => setAvatar("")}>Remove</Button>}
              <input ref={fileRef} type="file" accept="image/*" hidden onChange={onFile} />
            </div>
          </div>
          <div>
            <Label className="text-xs">Header title</Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. The Orlando Magic Hotel" data-testid="widget-title-input" className="mt-1" />
          </div>
          <div>
            <Label className="text-xs">Welcome message</Label>
            <Textarea value={welcome} onChange={(e) => setWelcome(e.target.value)} rows={3} placeholder="Hi! How can we help you plan your stay?" data-testid="widget-welcome-input" className="mt-1" />
            <p className="text-[11px] text-muted-foreground mt-1">Leave blank to use the default multilingual greeting.</p>
          </div>
        </div>
        <DialogFooter>
          <Button onClick={save} disabled={saving} data-testid="widget-save-btn">{saving ? "Saving…" : "Save changes"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function ChangePasswordCard() {
  const [cur, setCur] = useState("");
  const [nw, setNw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (nw.length < 8) return toast.error("New password must be at least 8 characters");
    if (nw !== confirm) return toast.error("New passwords don't match");
    setSaving(true);
    try {
      await api.post("/auth/change-password", { current_password: cur, new_password: nw });
      toast.success("Password changed");
      setCur(""); setNw(""); setConfirm("");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    } finally { setSaving(false); }
  };

  return (
    <div className="rounded-lg border border-border bg-card p-5 max-w-md" data-testid="change-password-card">
      <h3 className="font-heading font-semibold text-sm mb-1">Change password</h3>
      <p className="text-xs text-muted-foreground mb-4">Update your account password. Use a strong, unique password — especially on your live site.</p>
      <div className="space-y-3">
        <div><Label className="text-xs">Current password</Label><Input type="password" value={cur} onChange={(e) => setCur(e.target.value)} data-testid="cur-password-input" className="mt-1" autoComplete="current-password" /></div>
        <div><Label className="text-xs">New password</Label><Input type="password" value={nw} onChange={(e) => setNw(e.target.value)} data-testid="new-password-input" className="mt-1" autoComplete="new-password" /></div>
        <div><Label className="text-xs">Confirm new password</Label><Input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="confirm-password-input" className="mt-1" autoComplete="new-password" /></div>
        <div className="flex justify-end">
          <Button onClick={submit} disabled={!cur || !nw || !confirm || saving} data-testid="change-password-btn">{saving ? "Saving…" : "Update password"}</Button>
        </div>
      </div>
    </div>
  );
}

function PmsSync({ pmsStatus }) {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [json, setJson] = useState("");
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const configured = !!pmsStatus?.ohip_configured;
  const autoOn = !!pmsStatus?.auto_sync_enabled;
  const lastSync = pmsStatus?.last_sync ? new Date(pmsStatus.last_sync).toLocaleString() : null;

  const testConn = async () => {
    setTesting(true); setTestResult(null);
    try {
      const { data } = await api.post("/pms/test");
      setTestResult(data);
      if (data.ok) toast.success("OPERA Cloud connection OK");
      else toast.error("Connection failed — see details");
    } catch (e) { setTestResult({ ok: false, message: formatApiErrorDetail(e?.response?.data?.detail) || "Test failed" }); }
    finally { setTesting(false); }
  };

  const syncLive = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/pms/sync");
      toast.success(`Synced from OPERA Cloud · ${data.created} new, ${data.updated} updated, ${data.stays || 0} stays`);
      qc.invalidateQueries({ queryKey: ["pms-status"] });
    } catch (e) { toast.error(e?.response?.data?.detail || "OHIP sync failed"); }
    finally { setBusy(false); }
  };

  const toggleAuto = async (val) => {
    try {
      await api.put("/pms/settings", { enabled: val, hour: pmsStatus?.auto_sync_hour ?? 3 });
      toast.success(val ? "Nightly OPERA Cloud sync enabled" : "Nightly sync disabled");
      qc.invalidateQueries({ queryKey: ["pms-status"] });
    } catch { toast.error("Could not update sync setting"); }
  };

  const importJson = async () => {
    let profiles;
    try {
      const parsed = JSON.parse(json);
      profiles = Array.isArray(parsed) ? parsed : parsed.profiles || [parsed];
    } catch { return toast.error("Invalid JSON"); }
    setBusy(true);
    try {
      const { data } = await api.post("/pms/ingest", { profiles });
      toast.success(`Imported · ${data.created} new, ${data.updated} updated, ${data.skipped} skipped`);
      setJson(""); setShowImport(false);
      qc.invalidateQueries({ queryKey: ["pms-status"] });
    } catch (e) { toast.error(e?.response?.data?.detail || "Import failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="rounded-lg border border-border bg-card p-5 fade-up" data-testid="pms-sync-card" style={{ animationDelay: "80ms" }}>
      <div className="flex items-center gap-2 text-muted-foreground text-[11px] uppercase tracking-[0.14em] font-semibold mb-3"><CloudArrowDown size={14} /> PMS guest sync</div>
      <p className="text-sm text-muted-foreground mb-3">
        Pull guest profiles — including their preferred <b>language</b>, contact details, loyalty and <b>stay revenue</b> — from OPERA Cloud via OHIP.
        {configured
          ? <span className="text-success"> Connected to hotel {pmsStatus?.hotel_id}{pmsStatus?.environment ? ` · ${pmsStatus.environment}` : ""}.</span>
          : <span> Add OHIP credentials to go live, or import a profile export now.</span>}
      </p>

      {user?.is_admin && (
        <div className="rounded-md border border-border px-3.5 py-3 mb-3" data-testid="ohip-connection-row">
          <div className="flex items-center justify-between">
            <div>
              <div className="font-medium text-sm">OPERA / OHIP connection</div>
              <div className="text-xs text-muted-foreground">
                {pmsStatus?.gateway ? <>Gateway <code className="text-[11px]">{pmsStatus.gateway}</code></> : "Not configured"}
                {pmsStatus?.environment ? ` · ${pmsStatus.environment}` : ""}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button data-testid="ohip-test-btn" size="sm" variant="outline" disabled={testing || !configured} onClick={testConn}>
                {testing ? "Testing…" : "Test connection"}
              </Button>
              <OhipConfigDialog config={pmsStatus?.config} onDone={() => qc.invalidateQueries({ queryKey: ["pms-status"] })} />
            </div>
          </div>
          {testResult && (
            <div data-testid="ohip-test-result" className={`mt-2.5 text-xs rounded-md px-3 py-2 ${testResult.ok ? "bg-success/10 text-success border border-success/20" : "bg-destructive/10 text-destructive border border-destructive/20"}`}>
              {testResult.message}
            </div>
          )}
        </div>
      )}

      {configured && (
        <div className="flex items-center justify-between rounded-md border border-border px-3.5 py-3 mb-3" data-testid="pms-autosync-row">
          <div>
            <div className="font-medium text-sm">Nightly auto-sync</div>
            <div className="text-xs text-muted-foreground">
              Runs daily at {String(pmsStatus?.auto_sync_hour ?? 3).padStart(2, "0")}:00 property time.
              {lastSync ? ` Last sync ${lastSync}.` : " Not run yet."}
            </div>
          </div>
          <Switch data-testid="pms-autosync-toggle" checked={autoOn} onCheckedChange={toggleAuto} />
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        <Button data-testid="pms-sync-btn" size="sm" className="gap-1.5" disabled={busy || !configured} onClick={syncLive} title={configured ? "" : "OHIP not configured"}>
          <CloudArrowDown size={14} /> {busy ? "Syncing…" : "Sync from OPERA Cloud"}
        </Button>
        <Button data-testid="pms-import-toggle" size="sm" variant="secondary" className="gap-1.5" onClick={() => setShowImport((s) => !s)}>
          <UploadSimple size={14} /> Import profiles (JSON)
        </Button>
      </div>
      {showImport && (
        <div className="mt-3 space-y-2">
          <Textarea data-testid="pms-import-json" value={json} onChange={(e) => setJson(e.target.value)} rows={6}
            placeholder='[{"profileId":"OP-1001","givenName":"Hiroshi","surname":"Tanaka","language":"JPN","emails":[{"value":"hiroshi@hotel.com"}],"loyalty":{"tier":"Gold"}}]'
            className="text-xs font-mono" />
          <Button data-testid="pms-import-btn" size="sm" disabled={busy || !json.trim()} onClick={importJson}>Import</Button>
        </div>
      )}
    </div>
  );
}

const OHIP_TEXT_FIELDS = [
  ["gateway_url", "Gateway URL", "https://xxxx.hospitality-api.us-ashburn-1.ocs.oc-test.com"],
  ["hotel_id", "Property / Hotel ID", "CSMFLA"],
  ["client_id", "Client ID", "NOMADE-UAT-NOMADEE-xxxx-MinibarPOS"],
  ["enterprise_id", "Enterprise ID", "NOMADEE"],
  ["scope", "Scope", "urn:opc:hgbu:ws:__myscopes__"],
  ["username", "Username (optional)", "leave blank for client-credentials"],
];
const OHIP_SECRET_FIELDS = [
  ["app_key", "App Key (x-app-key)"],
  ["client_secret", "Client Secret"],
  ["password", "Password (optional)"],
];

function OhipConfigDialog({ config, onDone }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  const reset = () => {
    const f = {};
    OHIP_TEXT_FIELDS.forEach(([k]) => { f[k] = config?.[k] || ""; });
    OHIP_SECRET_FIELDS.forEach(([k]) => { f[k] = ""; });
    setForm(f);
  };
  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      // Only send secret fields when the user actually typed a new value.
      const payload = {};
      OHIP_TEXT_FIELDS.forEach(([k]) => { payload[k] = (form[k] || "").trim(); });
      OHIP_SECRET_FIELDS.forEach(([k]) => { if ((form[k] || "").trim()) payload[k] = form[k].trim(); });
      const { data } = await api.put("/pms/config", payload);
      toast.success(data.ohip_configured ? "OHIP connection saved" : "Saved — still missing required fields");
      onDone();
      setOpen(false);
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Save failed"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (o) reset(); }}>
      <DialogTrigger asChild>
        <Button data-testid="ohip-edit-btn" size="sm" className="gap-1.5"><PencilSimple size={14} /> Edit connection</Button>
      </DialogTrigger>
      <DialogContent data-testid="ohip-config-dialog" className="max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>OPERA / OHIP connection</DialogTitle>
          <DialogDescription>Enter your OPERA Cloud gateway and OAuth credentials. Secrets are stored securely and never shown again — leave them blank to keep the current value.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {OHIP_TEXT_FIELDS.map(([k, label, ph]) => (
            <div key={k}>
              <Label className="text-xs mb-1 block">{label}</Label>
              <Input value={form[k] || ""} onChange={(e) => set(k, e.target.value)} placeholder={ph} data-testid={`ohip-${k}`} className="font-mono text-[12px]" />
            </div>
          ))}
          {OHIP_SECRET_FIELDS.map(([k, label]) => (
            <div key={k}>
              <Label className="text-xs mb-1 block">{label} {config?.[`${k}_set`] && <span className="text-success text-[10px]">· set</span>}</Label>
              <Input type="password" value={form[k] || ""} onChange={(e) => set(k, e.target.value)} placeholder={config?.[`${k}_set`] ? "•••••••• (unchanged)" : "Enter value"} data-testid={`ohip-${k}`} className="font-mono text-[12px]" />
            </div>
          ))}
        </div>
        <DialogFooter>
          <Button onClick={save} disabled={saving} data-testid="ohip-save-btn">{saving ? "Saving…" : "Save connection"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const TIMEZONES = [
  "America/New_York", "America/Chicago", "America/Denver", "America/Los_Angeles",
  "America/Phoenix", "Europe/London", "Europe/Paris", "Asia/Dubai", "Asia/Tokyo", "Australia/Sydney",
];
const HOURS = Array.from({ length: 24 }, (_, h) => h);
const fmtHour = (h) => { const ap = h < 12 ? "AM" : "PM"; const hh = h % 12 === 0 ? 12 : h % 12; return `${hh}:00 ${ap}`; };

function MessagingSchedule({ property }) {
  const [tz, setTz] = useState(property?.timezone || "America/New_York");
  const [start, setStart] = useState(property?.quiet_hours_start ?? 9);
  const [end, setEnd] = useState(property?.quiet_hours_end ?? 18);
  const [sms, setSms] = useState(property?.sms_enabled ?? true);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (Number(end) <= Number(start)) {
      toast.error("'Send until' must be later than 'Send from'");
      return;
    }
    setSaving(true);
    try {
      await api.put("/property/settings", { timezone: tz, quiet_hours_start: Number(start), quiet_hours_end: Number(end), sms_enabled: sms });
      toast.success("Messaging schedule saved");
    } catch { toast.error("Could not save settings"); }
    finally { setSaving(false); }
  };

  return (
    <div className="rounded-lg border border-border bg-card p-5 fade-up">
      <div className="flex items-center gap-2 text-muted-foreground text-[11px] uppercase tracking-[0.14em] font-semibold mb-3"><Clock size={14} /> Messaging schedule</div>
      <p className="text-sm text-muted-foreground mb-3">Automated emails & SMS only send inside this window, in your hotel's timezone.</p>
      <div className="space-y-3">
        <div>
          <label className="text-[13px] font-medium mb-1.5 block">Timezone</label>
          <Select value={tz} onValueChange={setTz}>
            <SelectTrigger data-testid="settings-timezone"><SelectValue /></SelectTrigger>
            <SelectContent>{TIMEZONES.map((z) => <SelectItem key={z} value={z}>{z.replace("_", " ")}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-[13px] font-medium mb-1.5 block">Send from</label>
            <Select value={String(start)} onValueChange={(v) => setStart(Number(v))}>
              <SelectTrigger data-testid="settings-window-start"><SelectValue /></SelectTrigger>
              <SelectContent>{HOURS.map((h) => <SelectItem key={h} value={String(h)}>{fmtHour(h)}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-[13px] font-medium mb-1.5 block">Send until</label>
            <Select value={String(end)} onValueChange={(v) => setEnd(Number(v))}>
              <SelectTrigger data-testid="settings-window-end"><SelectValue /></SelectTrigger>
              <SelectContent>{HOURS.map((h) => <SelectItem key={h} value={String(h)}>{fmtHour(h)}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
        <div className="flex items-center justify-between rounded-md border border-border px-3.5 py-2.5">
          <div className="flex items-center gap-2"><DeviceMobile size={16} className="text-blue-600" /><span className="text-sm font-medium">SMS messaging</span></div>
          <Switch data-testid="settings-sms-toggle" checked={sms} onCheckedChange={setSms} />
        </div>
        <Button data-testid="save-schedule-btn" className="w-full" disabled={saving} onClick={save}>{saving ? "Saving…" : "Save schedule"}</Button>
      </div>
    </div>
  );
}

function EditPropertyDialog({ property }) {
  const { refreshProperty } = useAuth();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState(property.name || "");
  const [address, setAddress] = useState(property.address || "");
  const [color, setColor] = useState(property.brand_color || "#EA580C");
  const [preview, setPreview] = useState(null);
  const [logoUrl, setLogoUrl] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  const onPick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) return toast.error("Please choose an image file");
    if (file.size > 5 * 1024 * 1024) return toast.error("Image too large (max 5 MB)");
    setPreview(URL.createObjectURL(file));
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/property/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setLogoUrl(data.url);
      toast.success("Logo ready — click Save to apply");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
      setPreview(null);
    } finally { setUploading(false); }
  };

  const save = async () => {
    if (!name.trim()) return toast.error("Name is required");
    setSaving(true);
    try {
      const payload = { name: name.trim(), address: address.trim(), brand_color: color };
      if (logoUrl) payload.image = logoUrl;
      await api.put("/property", payload);
      await refreshProperty();
      toast.success("Property details saved");
      setOpen(false);
      setPreview(null);
      setLogoUrl(null);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const currentImg = preview || resolveImg(property.image);

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (o) { setName(property.name || ""); setAddress(property.address || ""); setColor(property.brand_color || "#EA580C"); setPreview(null); setLogoUrl(null); } }}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" data-testid="edit-property-btn"><PencilSimple size={14} className="mr-1.5" /> Edit</Button>
      </DialogTrigger>
      <DialogContent data-testid="edit-property-dialog">
        <DialogHeader>
          <DialogTitle>Edit property details</DialogTitle>
          <DialogDescription>Update your hotel's name, address, brand color and logo.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-1">
          <div>
            <Label className="text-xs">Logo / cover image</Label>
            <div className="mt-1.5 flex items-center gap-3">
              <div className="h-16 w-24 rounded-md border border-border bg-secondary overflow-hidden shrink-0">
                {currentImg && <img src={currentImg} alt="" className="h-full w-full object-cover" data-testid="logo-preview" />}
              </div>
              <input ref={fileRef} type="file" accept="image/*" onChange={onPick} className="hidden" data-testid="logo-file-input" />
              <Button type="button" variant="secondary" size="sm" disabled={uploading} onClick={() => fileRef.current?.click()} data-testid="logo-upload-btn">
                <UploadSimple size={14} className="mr-1.5" /> {uploading ? "Uploading…" : "Upload image"}
              </Button>
            </div>
          </div>
          <div>
            <Label className="text-xs">Property name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} className="mt-1" data-testid="edit-name-input" />
          </div>
          <div>
            <Label className="text-xs">Address</Label>
            <Input value={address} onChange={(e) => setAddress(e.target.value)} className="mt-1" data-testid="edit-address-input" />
          </div>
          <div>
            <Label className="text-xs">Brand color</Label>
            <div className="mt-1 flex items-center gap-2">
              <input type="color" value={color} onChange={(e) => setColor(e.target.value)} className="h-9 w-12 rounded-md border border-border cursor-pointer bg-transparent" data-testid="edit-color-picker" />
              <Input value={color} onChange={(e) => setColor(e.target.value)} className="w-32 font-mono text-sm" data-testid="edit-color-input" />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button disabled={saving || uploading} onClick={save} data-testid="edit-property-save-btn">{saving ? "Saving…" : "Save changes"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Info({ label, value, swatch }) {
  return (
    <div className="rounded-md border border-border p-3">
      <div className="text-[11px] text-muted-foreground uppercase tracking-wide">{label}</div>
      <div className="text-sm font-medium mt-0.5 flex items-center gap-2">
        {swatch && <span className="h-3.5 w-3.5 rounded-sm border border-border" style={{ background: swatch }} />}
        {value}
      </div>
    </div>
  );
}
